/**
 * SMS du livreur — les trois messages que l'application livreur pré-remplit
 * (lien `sms:`, envoyés depuis le téléphone du livreur, pas de Twilio).
 *
 * Stockés dans `settings.ecommerce.livreurSms` et servis avec la tournée. Un
 * texte resté identique au défaut est enregistré VIDE : si le texte par défaut
 * change un jour, il s'appliquera sans qu'on ait à repasser ici.
 *
 * ⚠️ Textes par défaut et remplissage des repères : copie identique de
 * `driver/src/utils/sms.ts` (SMS_PAR_DEFAUT, remplirSms). Les deux bougent ensemble.
 */

export type CleSms = 'arrivee' | 'devant' | 'absent';
export type SmsLivreur = Record<CleSms, string>;

export const SMS_PAR_DEFAUT: SmsLivreur = {
  arrivee:
    "Les Folies Maraîchères — Bonjour {prénom}, ici {livreur}. J'arrive chez vous {quand} avec votre commande. {règlement}",
  devant: 'Les Folies Maraîchères — Bonjour {prénom}, ici {livreur} : je suis devant chez vous avec votre commande.',
  absent:
    "Les Folies Maraîchères — Bonjour {prénom}, ici {livreur}. Je suis passé avec votre commande mais personne n'a répondu. Rappelez-moi à ce numéro pour convenir d'un moment.",
};

const MESSAGES: { cle: CleSms; titre: string; quand: string }[] = [
  { cle: 'arrivee', titre: "J'arrive", quand: 'En route vers le client' },
  { cle: 'devant', titre: 'Je suis devant chez vous', quand: 'Arrivé à la porte' },
  { cle: 'absent', titre: 'Personne n’a répondu', quand: 'Client absent' },
];

const REPERES: { code: string; sens: string }[] = [
  { code: '{prénom}', sens: 'prénom du client (vide pour un bon Enfact)' },
  { code: '{livreur}', sens: '« Philippe, votre livreur »' },
  { code: '{quand}', sens: '« vers 10 h 30 » ou « dans quelques minutes »' },
  { code: '{règlement}', sens: 'montant à encaisser, vide si déjà payé' },
];

const EXEMPLE: Record<string, string> = {
  prenom: 'Marie',
  livreur: 'Philippe, votre livreur',
  quand: 'vers 10 h 30',
  reglement: 'À régler à la livraison : 12,30 € (Bancontact ou espèces).',
};

function cleRepere(nom: string): string {
  return nom.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

export function remplirSms(modele: string, valeurs: Record<string, string>): string {
  const VIDE = '\u0000';
  return modele
    .replace(/\{([^{}]+)\}/g, (tout, nom: string) => {
      const v = valeurs[cleRepere(nom)];
      if (v === undefined) return tout;
      return v === '' ? VIDE : v;
    })
    .replace(/[ \t]*\u0000/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

/** Ce qui est lu des réglages → texte affiché (le défaut quand rien n'est réglé). */
export function smsDepuisReglages(bloc: any): SmsLivreur {
  const lire = (cle: CleSms) => {
    const v = bloc && typeof bloc === 'object' ? bloc[cle] : undefined;
    return typeof v === 'string' && v.trim() ? v : SMS_PAR_DEFAUT[cle];
  };
  return { arrivee: lire('arrivee'), devant: lire('devant'), absent: lire('absent') };
}

/** Texte affiché → ce qui est enregistré (vide = texte par défaut). */
export function smsPourReglages(sms: SmsLivreur): SmsLivreur {
  const garder = (cle: CleSms) => {
    const v = sms[cle].trim();
    return v === SMS_PAR_DEFAUT[cle] ? '' : v;
  };
  return { arrivee: garder('arrivee'), devant: garder('devant'), absent: garder('absent') };
}

function reperesInconnus(texte: string): string[] {
  const connus = new Set(Object.keys(EXEMPLE));
  return [...texte.matchAll(/\{([^{}]+)\}/g)].map((m) => m[0]).filter((r) => !connus.has(cleRepere(r.slice(1, -1))));
}

export default function SmsLivreurSettings({
  value,
  onChange,
}: {
  value: SmsLivreur;
  onChange: (v: SmsLivreur) => void;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
      <div>
        <h3 className="text-lg font-bold text-gray-900">📱 SMS du livreur</h3>
        <p className="text-sm text-gray-600 mt-1">
          L'application livreur prépare ces messages ; le livreur les relit et les envoie depuis son
          téléphone. Le client voit son numéro et peut le rappeler. Les mots entre accolades sont
          remplacés au moment de l'envoi.
        </p>
        <ul className="mt-3 grid gap-1 sm:grid-cols-2 text-sm">
          {REPERES.map((r) => (
            <li key={r.code} className="text-gray-600">
              <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-900">{r.code}</code> {r.sens}
            </li>
          ))}
        </ul>
      </div>

      {MESSAGES.map(({ cle, titre, quand }) => {
        const texte = value[cle];
        const modifie = texte.trim() !== SMS_PAR_DEFAUT[cle];
        const inconnus = reperesInconnus(texte);
        return (
          <div key={cle} className="space-y-2">
            <div className="flex items-baseline justify-between gap-3">
              <label htmlFor={`sms-${cle}`} className="font-medium text-gray-900">
                {titre} <span className="text-sm font-normal text-gray-500">· {quand}</span>
              </label>
              {modifie && (
                <button
                  type="button"
                  onClick={() => onChange({ ...value, [cle]: SMS_PAR_DEFAUT[cle] })}
                  className="text-sm text-blue-700 hover:underline shrink-0"
                >
                  Rétablir le texte d'origine
                </button>
              )}
            </div>
            <textarea
              id={`sms-${cle}`}
              rows={3}
              value={texte}
              onChange={(e) => onChange({ ...value, [cle]: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {inconnus.length > 0 && (
              <p className="text-sm text-amber-700">
                {inconnus.join(', ')} : repère inconnu, il partira tel quel. Vérifiez l'orthographe.
              </p>
            )}
            <p className="text-sm text-gray-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
              <span className="text-gray-500">Aperçu : </span>
              {remplirSms(texte.trim() || SMS_PAR_DEFAUT[cle], EXEMPLE)}
            </p>
          </div>
        );
      })}
    </div>
  );
}
